import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Typography, Divider, Pagination, Empty, Spin, Space, Tag, Input } from 'antd';
import { ArrowLeftOutlined, CalendarOutlined, FileTextOutlined, DollarOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import lib from '../../../assets/utils/lib';
import { notification } from 'antd';
// Import ClaimCard from facilityClaims - we'll create a simpler version if needed
// For now, we'll use a basic card display
import './BatchDetailView.css';

const { Title, Text } = Typography;

/**
 * BatchDetailView - Displays claims within a specific batch (encounter date)
 * 
 * @param {Object} props
 * @param {Object} props.batch - Batch data { encounter_date, claims_count, total_cost }
 * @param {string} props.encounterDate - Encounter date (if batch is null, fetch by date)
 * @param {Function} props.onBack - Handler to go back to batch list
 */
const BatchDetailView = ({ batch, encounterDate, onBack }) => {
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [filteredClaims, setFilteredClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const targetDate = batch?.encounter_date || encounterDate || batch?.key;

  // Filter claims based on search
  useEffect(() => {
    if (!searchValue) {
      setFilteredClaims(claims);
    } else {
      const filtered = claims.filter(claim => {
        const searchLower = searchValue.toLowerCase();
        return (
          (claim.checkin_id && claim.checkin_id.toLowerCase().includes(searchLower)) ||
          (claim.claims_id && claim.claims_id.toLowerCase().includes(searchLower)) ||
          (claim.surname && claim.surname.toLowerCase().includes(searchLower)) ||
          (claim.first_name && claim.first_name.toLowerCase().includes(searchLower)) ||
          (claim.insurance_no && claim.insurance_no.toLowerCase().includes(searchLower))
        );
      });
      setFilteredClaims(filtered);
    }
  }, [claims, searchValue]);

  const fetchBatchClaims = useCallback(async (date, page = 1) => {
    setLoading(true);
    try {
      const response = await lib.getBatchClaims(date, {
        page,
        pageSize: pagination.pageSize,
      });

      if (response?.data?.error) {
        notification.error({
          message: 'Error',
          description: response.data.message || 'Failed to fetch claims',
        });
        return;
      }

      // Backend returns: { error, message, data: { claims: [...], pagination: {...} } }
      // or for legacy format: { error, message, data: [...claims...], pagination: {...} }
      const responseData = response?.data || {};
      const data = responseData.data || {};
      
      // Handle both formats: data.claims (grouped) or data as array (legacy)
      const claimsList = Array.isArray(data.claims) 
        ? data.claims 
        : (Array.isArray(data) ? data : []);
      
      setClaims(claimsList);
      setPagination((prev) => ({
        ...prev,
        current: responseData.pagination?.page || data.pagination?.page || page,
        pageSize: responseData.pagination?.pageSize || data.pagination?.pageSize || prev.pageSize,
        total: responseData.pagination?.total || data.pagination?.total || claimsList.length || 0,
      }));
    } catch (error) {
      console.error('Error fetching batch claims:', error);
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message || 'Failed to fetch claims',
      });
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize]);

  useEffect(() => {
    if (targetDate) {
      fetchBatchClaims(targetDate, pagination.current);
    }
  }, [targetDate, pagination, fetchBatchClaims]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({ ...prev, current: page, pageSize }));
  };

  const formatEncounterDate = (dateString) => {
    return dayjs(dateString).format('MMMM D, YYYY');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  if (!targetDate) {
    return (
      <div className="batch-detail-view">
        <Empty description="No batch selected" />
      </div>
    );
  }

  return (
    <div className="batch-detail-view">
      {/* Back button */}
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={onBack}
        className="batch-detail-view__back-button"
      >
        Back to Batches
      </Button>

      {/* Batch header */}
      <div className="batch-detail-view__header">
        <div className="batch-detail-view__header-title">
          <CalendarOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
          <Title level={2} style={{ margin: 0 }}>
            Encounter Date: {formatEncounterDate(targetDate)}
          </Title>
        </div>
        <div className="batch-detail-view__header-summary">
          <Space size="large">
            <div>
              <FileTextOutlined style={{ marginRight: '4px' }} />
              <Text strong>{batch?.claims_count || claims.length}</Text>
              <Text type="secondary" style={{ marginLeft: '4px' }}>
                {batch?.claims_count === 1 ? 'claim' : 'claims'}
              </Text>
            </div>
            <Divider type="vertical" />
            <div>
              <DollarOutlined style={{ marginRight: '4px' }} />
              <Text strong style={{ color: '#52c41a' }}>
                {formatCurrency(batch?.total_cost || 0)}
              </Text>
            </div>
          </Space>
        </div>
      </div>

      <Divider />

      {/* Search and Export */}
      <div className="batch-detail-view__actions">
        <Input
          placeholder="Search claims by ID, name, or insurance number..."
          prefix={<SearchOutlined />}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          allowClear
          style={{ maxWidth: '400px' }}
        />
      </div>

      {/* Claims list */}
      <div className="batch-detail-view__claims">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
          </div>
        ) : filteredClaims.length === 0 ? (
          <Empty
            description={
              <div>
                <Text type="secondary">
                  {searchValue 
                    ? `No claims found matching "${searchValue}"` 
                    : 'No claims found for this encounter date.'}
                </Text>
              </div>
            }
          />
        ) : (
          <>
            <div className="batch-detail-view__claims-list">
              {filteredClaims.map((claim) => (
                <div
                  key={claim.checkin_id || claim.id}
                  className="batch-claim-item"
                  onClick={() => {
                    // Navigate to claim details - adjust this based on your routing
                    // navigate(`/claims/filings/${btoa(claim.facility_id)}/${btoa(segment)}`);
                    console.log('Navigate to claim:', claim);
                  }}
                  style={{
                    padding: '16px',
                    marginBottom: '12px',
                    background: '#fff',
                    borderRadius: '8px',
                    border: '1px solid #e8e8e8',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text strong>
                        {claim.checkin_id || claim.claims_id || 'N/A'}
                      </Text>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {claim.claims_date ? new Date(claim.claims_date).toLocaleDateString() : ''}
                      </Text>
                    </div>
                    <div>
                      <Text>
                        {claim.surname || ''} {claim.first_name || ''} {claim.middle_name || ''}
                      </Text>
                    </div>
                    {claim.total_cost && (
                      <div>
                        <Text strong style={{ color: '#52c41a' }}>
                          {new Intl.NumberFormat('en-NG', {
                            style: 'currency',
                            currency: 'NGN',
                          }).format(claim.total_cost)}
                        </Text>
                      </div>
                    )}
                  </Space>
                </div>
              ))}
            </div>

            {pagination.total > pagination.pageSize && (
              <div className="batch-detail-view__pagination">
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={searchValue ? filteredClaims.length : pagination.total}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                  showQuickJumper
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} of ${total} claims${searchValue ? ' (filtered)' : ''}`
                  }
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BatchDetailView;

